import { DOCS_SECTIONS, pageRoute } from "features/Docs/manifest"
import { createSlugger, cleanHeadingText } from "features/Docs/utils/slugs"
import {
  DocsHeading,
  DocsParsed,
  SearchRecord,
  SearchResult,
} from "features/Docs/types/docs.types"

/**
 * Client-side search over the loaded docs content: build one record per
 * present page, then score records against a query. Field weights favour
 * titles and author-supplied keywords over headings and body text, and a
 * whole-word match outscores a prefix, which outscores a loose substring.
 */

const FIELD_WEIGHT = {
  title: 8,
  keywords: 5,
  headings: 3,
  body: 1,
} as const

const escapeRegExp = (term: string): string =>
  term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * Per-field match strength for a single term:
 * whole word (1) > word-prefix (0.6) > substring (0.3) > none (0).
 */
const matchStrength = (text: string, term: string): number => {
  if (!text) return 0
  const haystack = text.toLowerCase()
  const escaped = escapeRegExp(term)
  if (new RegExp(`\\b${escaped}\\b`).test(haystack)) return 1
  if (new RegExp(`\\b${escaped}`).test(haystack)) return 0.6
  if (haystack.includes(term)) return 0.3
  return 0
}

/** Remove fenced code blocks so code comments aren't mistaken for headings. */
const stripCodeFences = (body: string): string =>
  body.replace(/```[\s\S]*?```/g, "")

/**
 * Flatten markdown to plain text for body search. Code fence *content* is
 * kept (so command names remain searchable); only the fence markers, inline
 * code backticks, link URLs, and emphasis punctuation are removed.
 */
const stripMarkdown = (body: string): string =>
  body
    .replace(/```[\w-]*\r?\n?/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_>#|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()

/** Extract `##`–`####` headings (outside code) with de-duplicated slugs. */
const extractHeadings = (body: string): DocsHeading[] => {
  const slugger = createSlugger()
  const headings: DocsHeading[] = []
  const regex = /^(#{2,4})\s+(.+?)\s*#*$/gm
  let match: RegExpExecArray | null
  while ((match = regex.exec(body)) !== null) {
    const text = cleanHeadingText(match[2].trim())
    headings.push({ text, slug: slugger.slug(text), depth: match[1].length })
  }
  return headings
}

/** Build the search index from the loaded content map. */
export const buildSearchIndex = (
  content: Map<string, DocsParsed>,
): SearchRecord[] => {
  const records: SearchRecord[] = []
  DOCS_SECTIONS.forEach((section) => {
    section.pages.forEach((page) => {
      const parsed = content.get(page.file)
      if (!parsed) return
      records.push({
        route: pageRoute(section.slug, page.slug),
        sectionSlug: section.slug,
        sectionTitle: section.title,
        pageSlug: page.slug,
        pageTitle: parsed.meta.title ?? page.title,
        description: parsed.meta.description ?? "",
        keywords: parsed.meta.keywords,
        headings: extractHeadings(stripCodeFences(parsed.body)),
        plainBody: stripMarkdown(parsed.body),
      })
    })
  })
  return records
}

/** Best keyword match strength for a term across a record's keywords. */
const keywordStrength = (keywords: string[], term: string): number =>
  keywords.reduce((best, keyword) => Math.max(best, matchStrength(keyword, term)), 0)

/**
 * Score records against a query and return matches, highest score first.
 * When a heading matched, its slug is attached for deep-linking.
 */
export const searchDocs = (
  records: SearchRecord[],
  rawQuery: string,
): SearchResult[] => {
  const terms = rawQuery.toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return []

  const results: SearchResult[] = []

  records.forEach((record) => {
    let score = 0
    let headingSlug: string | undefined
    let bestHeadingScore = 0

    terms.forEach((term) => {
      score += FIELD_WEIGHT.title * matchStrength(record.pageTitle, term)
      score += FIELD_WEIGHT.title * 0.5 * matchStrength(record.sectionTitle, term)
      score += FIELD_WEIGHT.keywords * keywordStrength(record.keywords, term)

      record.headings.forEach((heading) => {
        const strength = matchStrength(heading.text, term)
        if (strength <= 0) return
        const contribution = FIELD_WEIGHT.headings * strength
        score += contribution
        if (contribution > bestHeadingScore) {
          bestHeadingScore = contribution
          headingSlug = heading.slug
        }
      })

      score += FIELD_WEIGHT.body * matchStrength(record.plainBody, term)
      score += FIELD_WEIGHT.body * matchStrength(record.description, term)
    })

    if (score > 0) results.push({ record, score, headingSlug })
  })

  return results.sort((a, b) => b.score - a.score)
}
